<template>
  <nav class="nav-bar">
    <!-- Hamburger icon for mobile -->
    <div class="hamburger" @click="toggleMobileMenu">
      <span></span>
      <span></span>
      <span></span>
    </div>

    <!-- Desktop Navigation Links -->
    <ul class="nav-links desktop-links">
      <li>
        <router-link to="/menu" class="nav-link" active-class="active-link">
          Menu
        </router-link>
      </li>
      <li>
        <router-link to="/editor" class="nav-link" active-class="active-link">
          Dish Editor
        </router-link>
      </li>
      <li>
        <router-link to="/settings" class="nav-link" active-class="active-link">
          User Settings
        </router-link>
      </li>
      <li>
        <router-link to="/contact" class="nav-link" active-class="active-link">
          Contact
        </router-link>
      </li>
      <!-- New Help Icon Item -->
      <li>
        <a href="#" class="nav-link" @click.prevent="openHelpModal">
          <svg class="help-icon" viewBox="0 0 512 512" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
            <path d="M256 8C119.043 8 8 119.083 8 256c0 136.997 111.043 248 248 248s248-111.003 248-248C504 119.083 392.957 8 256 8zm0 448c-110.532 0-200-89.431-200-200 0-110.495 89.472-200 200-200 110.491 0 200 89.471 200 200 0 110.53-89.431 200-200 200zm107.244-255.2c0 67.052-72.421 68.084-72.421 92.863V300c0 6.627-5.373 12-12 12h-45.647c-6.627 0-12-5.373-12-12v-8.659c0-35.745 27.1-50.034 47.579-61.516 17.561-9.845 28.324-16.541 28.324-29.579 0-17.246-21.999-28.693-39.784-28.693-23.189 0-33.894 10.977-48.942 29.969-4.057 5.12-11.46 6.071-16.666 2.124l-27.824-21.098c-5.107-3.872-6.251-11.066-2.644-16.363C184.846 131.491 214.94 112 261.794 112c49.071 0 101.45 38.304 101.45 88.8zM298 368c0 23.159-18.841 42-42 42s-42-18.841-42-42 18.841-42 42-42 42 18.841 42 42z" fill="#464646"/>
          </svg>
        </a>
      </li>
    </ul>

    <!-- Desktop Auth Links -->
    <div class="auth-links desktop-auth">
      <a v-if="isAuthenticated" href="#" class="auth-link logout-link" @click.prevent="handleLogout">
        Logout
      </a>
      <a v-else href="#" class="auth-link login-register-link" @click.prevent="toggleAuthModal">
        Login/Register
      </a>
    </div>

    <!-- Mobile Menu Overlay -->
    <transition name="slide-fade">
      <div v-if="showMenu" class="mobile-menu" @click.self="closeMobileMenu">
        <div class="mobile-menu-content">
          <ul class="mobile-nav-links">
            <li>
              <router-link to="/menu" class="nav-link" active-class="active-link" @click="closeMobileMenu">
                Menu
              </router-link>
            </li>
            <li>
              <router-link to="/editor" class="nav-link" active-class="active-link" @click="closeMobileMenu">
                Dish Editor
              </router-link>
            </li>
            <li>
              <router-link to="/settings" class="nav-link" active-class="active-link" @click="closeMobileMenu">
                User Settings
              </router-link>
            </li>
            <li>
              <router-link to="/contact" class="nav-link" active-class="active-link" @click="closeMobileMenu">
                Contact
              </router-link>
            </li>
            <!-- New Help Item in Mobile Menu -->
            <li>
              <a href="#" class="nav-link" @click.prevent="openHelpModal">Help</a>
            </li>
          </ul>
          <div class="mobile-auth-links">
            <a v-if="isAuthenticated" href="#" class="auth-link logout-link" @click.prevent="handleLogout">
              Logout
            </a>
            <a v-else href="#" class="auth-link login-register-link" @click.prevent="() => { toggleAuthModal(); closeMobileMenu(); }">
              Login/Register
            </a>
          </div>
        </div>
      </div>
    </transition>

    <!-- Login/Register Modal -->
    <div v-if="showAuthModal" class="modal">
      <div class="modal-content">
        <h2>{{ isLogin ? "Login" : "Register" }}</h2>
        <form @submit.prevent="isLogin ? handleLogin() : handleRegister()">
          <input v-model="authData.email" type="email" placeholder="Email" required autocomplete="email" />
          <input
            v-model="authData.password"
            type="password"
            placeholder="Password"
            required
            :autocomplete="isLogin ? 'current-password' : 'new-password'"
          />
          <input
            v-if="!isLogin"
            v-model="authData.name"
            type="text"
            placeholder="Name"
            required
            autocomplete="name"
          />
          <!-- EULA section for registration -->
          <div v-if="!isLogin" class="eula-section">
            <label>
              <input type="checkbox" v-model="eulaAccepted" />
              I accept the <a href="#" @click.prevent="openEula">EULA</a>.
            </label>
            <p v-if="!eulaAccepted" class="eula-error">You must accept the EULA to register.</p>
          </div>
          <button type="submit" :disabled="!isLogin && !eulaAccepted" :class="{ 'disabled-button': !isLogin && !eulaAccepted }">
            {{ isLogin ? "Login" : "Register" }}
          </button>
          <button type="button" @click="toggleAuthMode">
            {{ isLogin ? "Create a user" : "Back to Login" }}
          </button>
          <div v-if="isLogin" class="forgot-password-container">
            <a href="#" class="forgot-password" @click.prevent="handleForgotPassword">Forgot Password?</a>
          </div>
          <button type="button" @click="toggleAuthModal" class="cancel-button">Cancel</button>
        </form>
      </div>
    </div>

    <!-- Loading Modal -->
    <div v-if="loading" class="modal">
      <div class="modal-content">
        <div class="spinner"></div>
        <p>Creating user...</p>
      </div>
    </div>

    <!-- EULA Modal -->
    <div v-if="showEula" class="modal">
      <div class="modal-content eula-modal">
        <h2>End-User License Agreement (EULA)</h2>
        <div class="eula-content" v-html="parsedEulaContent"></div>
        <button @click="showEula = false" class="close-eula-button">Close</button>
      </div>
    </div>

    <!-- Help Modal -->
    <HelpModal v-model:show="showHelpModal" />
  </nav>
</template>

<script>
import { ref, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useNotification } from "@/services/notificationService";
import { eulaContent } from "@/assets/eulaContent";
import {
  signIn,
  register,
  fetchOrCreateUserSettings,
  initializeUserDishes,
} from "@/services/firebaseFunctions";
import { auth, signOut } from "@/services/firebase";
import { sendPasswordResetEmail } from "firebase/auth";
import HelpModal from "@/components/HelpModal.vue";

export default {
  name: "NavBar",
  components: { HelpModal },
  setup() {
    const router = useRouter();
    const { displayNotification } = useNotification();

    const isAuthenticated = ref(false);
    const showAuthModal = ref(false);
    const showEula = ref(false);
    const isLogin = ref(true);
    const authData = ref({ email: "", password: "", name: "" });
    const loading = ref(false);
    const eulaAccepted = ref(false);
    const showMenu = ref(false);
    const showHelpModal = ref(false);

    // The EULA is a static document almost nobody opens, so marked is loaded
    // on demand rather than shipped in the main bundle and run on every page
    // load. Its input is a hardcoded constant, never user content - that is
    // what makes the v-html below safe.
    const parsedEulaContent = ref("");
    const openEula = async () => {
      if (!parsedEulaContent.value) {
        const { marked } = await import(/* webpackChunkName: "eula" */ "marked");
        parsedEulaContent.value = marked(eulaContent);
      }
      showEula.value = true;
    };

    const toggleMobileMenu = () => {
      showMenu.value = !showMenu.value;
    };
    const closeMobileMenu = () => {
      showMenu.value = false;
    };
    const openHelpModal = () => {
      showHelpModal.value = true;
      closeMobileMenu();
    };
    const toggleAuthModal = () => {
      showAuthModal.value = !showAuthModal.value;
    };
    const toggleAuthMode = () => {
      isLogin.value = !isLogin.value;
    };
    const handleLogin = async () => {
      try {
        const user = await signIn(authData.value.email, authData.value.password);
        isAuthenticated.value = !!user;
        toggleAuthModal();
        displayNotification("Login successful!");
      } catch (error) {
        displayNotification("Login failed: " + error.message);
      }
    };
    const handleRegister = async () => {
      try {
        loading.value = true;
        const user = await register(authData.value.email, authData.value.password, {
          name: authData.value.name,
          eulaAcceptedDate: new Date().toISOString(),
        });
        isAuthenticated.value = !!user;
        await fetchOrCreateUserSettings();
        await initializeUserDishes(user.uid);
        toggleAuthModal();
        displayNotification(
          "Registration successful! Check your inbox to verify your email address."
        );
      } catch (error) {
        displayNotification("Registration failed: " + error.message);
      } finally {
        loading.value = false;
      }
    };
    const handleLogout = async () => {
      try {
        await signOut(auth);
        isAuthenticated.value = false;
        router.push("/menu");
        displayNotification("You have been logged out.");
      } catch (error) {
        displayNotification("Failed to log out: " + error.message);
      }
    };
    const handleForgotPassword = async () => {
      if (!authData.value.email) {
        displayNotification("Please enter your email first!");
        return;
      }
      try {
        await sendPasswordResetEmail(auth, authData.value.email);
        displayNotification("Password reset email sent. Check your inbox!");
      } catch (error) {
        displayNotification("Error sending password reset: " + error.message);
      }
    };
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      isAuthenticated.value = !!user;
    });
    onUnmounted(() => {
      unsubscribeAuth();
    });
    return {
      isAuthenticated,
      showAuthModal,
      showEula,
      isLogin,
      authData,
      loading,
      eulaAccepted,
      showMenu,
      showHelpModal,
      parsedEulaContent,
      openEula,
      toggleAuthModal,
      toggleAuthMode,
      toggleMobileMenu,
      closeMobileMenu,
      openHelpModal,
      handleLogin,
      handleRegister,
      handleLogout,
      handleForgotPassword,
    };
  },
};
</script>

<style scoped>
/**********************
  NAVBAR LAYOUT
**********************/
.nav-bar {
  background-color: var(--bg-secondary);
  color: var(--text-secondary);
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 1000;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

/**********************
  HAMBURGER ICON
**********************/
.hamburger {
  display: none; /* default desktop */
  flex-direction: column;
  cursor: pointer;
  gap: 6px;
  padding: 10px;
}
.hamburger span {
  display: block;
  width: 30px;
  height: 4px;
  background: var(--text-secondary);
  border-radius: 2px;
}

/**********************
  DESKTOP NAV LINKS
**********************/
.desktop-links {
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: flex;
  gap: 20px;
}

.desktop-auth {
  display: flex;
  gap: 15px;
}

.nav-link {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 16px;
}

.nav-link:hover {
  text-decoration: underline;
}

.help-icon {
  position: relative;
  top: -2px; /* Adjust this value as needed */
}

.active-link {
  font-weight: bold;
  color: #ffffff;
}

.auth-link {
  color: var(--text-secondary);
  text-decoration: none;
  font-size: 16px;
  margin-right: 20px;
}

.auth-link:hover {
  text-decoration: underline;
}

.login-register-link {
  color: #4caf50;
}

.login-register-link:hover {
  text-decoration: underline;
}

.logout-link {
  color: #ff4d4d;
}

.logout-link:hover {
  text-decoration: underline;
}

/**********************
  MOBILE MENU OVERLAY
**********************/
.mobile-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1500;
  display: flex;
  justify-content: flex-end;
}

.mobile-menu-content {
  background-color: #1e1e1e;
  width: 70%;
  max-width: 300px;
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
}

.mobile-nav-links {
  list-style: none;
  padding: 0;
  margin: 0 0 20px;
}
.mobile-nav-links li {
  margin-bottom: 15px;
}
.mobile-nav-links .nav-link {
  font-size: 18px;
  color: var(--text-secondary);
}

/* Mobile Help Item */
.mobile-nav-links li a.nav-link:hover {
  text-decoration: underline;
}

.mobile-auth-links {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.4s, transform 0.4s;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(50px);
}

/**********************
  MEDIA QUERIES
**********************/
@media (max-width: 768px) {
  .nav-bar {
    padding: 10px 15px;
  }
  .nav-link,
  .auth-link {
    font-size: 14px;
  }
  .hamburger {
    display: flex;
    margin-right: 10px;
  }
  .desktop-links,
  .desktop-auth {
    display: none;
  }
}

/****************======
  MODALS (Common)
**********************/
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.modal-content {
  background: #1e1e1e;
  padding: 20px;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  color: var(--text-primary);
}

.modal-content h2 {
  margin-bottom: 20px;
  color: var(--text-secondary);
}

.modal-content p {
  margin: 10px 0;
}

.modal-content a {
  color: #4caf50;
  text-decoration: none;
}

.modal-content a:hover {
  text-decoration: underline;
}

/* Spinner */
.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Warning Message */
.warning-message {
  background-color: rgba(0, 0, 0, 0.8);
  border: 1px solid #ff4d4d;
  color: #ff4d4d;
  padding: 8px 12px;
  border-radius: 5px;
  font-size: 0.9em;
  margin: 15px auto;
  max-width: 500px;
  text-align: center;
  position: relative;
}

.warning-close {
  position: absolute;
  top: 2px;
  right: 4px;
  background: transparent;
  border: none;
  color: #ff4d4d;
  font-size: 1.2em;
  cursor: pointer;
}

/* Mobile Modals */
@media (max-width: 768px) {
  .modal-content {
    max-width: 90%;
    padding: 15px;
  }
  .button {
    padding: 6px 12px;
    font-size: 13px;
  }
  .warning-message {
    position: fixed;
    top: 0;
    left: 10px;
    right: 10px;
    margin: 0 auto;
    z-index: 1100;
    font-size: 0.95em;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }
  .warning-close {
    position: absolute;
    top: 2px;
    right: 4px;
    background: transparent;
    border: none;
    color: #ff4d4d;
    font-size: 1.2em;
    cursor: pointer;
  }
}

/* Auth Modal Form Styles */
.modal-content form {
  display: flex;
  flex-direction: column;
  gap: 15px;
  text-align: left;
}

.modal-content input[type="email"],
.modal-content input[type="password"],
.modal-content input[type="text"] {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #444;
  background-color: #2a2a2a;
  color: var(--text-primary);
  font-size: 14px;
  width: 100%;
}

.modal-content button {
  padding: 10px;
  border-radius: 5px;
  border: none;
  background-color: #007bff;
  color: white;
  cursor: pointer;
  font-size: 14px;
  margin-top: 5px;
}

.modal-content button:hover {
  background-color: #0056b3;
}

.modal-content .cancel-button {
  background-color: #dc3545;
}

.modal-content .cancel-button:hover {
  background-color: #c82333;
}

.disabled-button {
  opacity: 0.7;
  cursor: not-allowed !important;
}

.disabled-button:hover {
  background-color: #007bff !important; /* Prevent hover effect */
}

/* EULA Section */
.eula-section {
  margin: 10px 0;
  font-size: 14px;
}

.eula-section label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.eula-section input[type="checkbox"] {
  width: 16px;
  height: 16px;
}

.eula-error {
  color: #dc3545;
  font-size: 12px;
  margin-top: 5px;
}

/* Forgot password */
.forgot-password-container {
  text-align: center;
  margin: 10px 0;
}

.forgot-password {
  color: #007bff;
  text-decoration: none;
  font-size: 14px;
}

.forgot-password:hover {
  text-decoration: underline;
}

/* EULA Modal */
.eula-modal {
  max-width: 700px;
  max-height: 80vh;
  overflow-y: auto;
}

.eula-content {
  text-align: left;
  padding: 10px;
  margin: 15px 0;
  border: 1px solid #444;
  border-radius: 5px;
  background-color: #2a2a2a;
  height: 300px;
  overflow-y: auto;
}

.close-eula-button {
  background-color: #007bff;
  color: white;
  padding: 8px 16px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 10px;
}

.close-eula-button:hover {
  background-color: #0056b3;
}
</style>
